// src/pages/EventsPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import DataGrid from '../components/DataGrid';

export default function EventsPage() {
  // [marker: step1-templates-grids v1] Excel-style events list (all events load; filter in the grid)
  const { isManagerOrAbove } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [upcomingOnly, setUpcomingOnly] = useState(() => {
    try { return localStorage.getItem('events:upcomingOnly') !== 'false'; } catch { return true; }
  });
  const [showModal, setShowModal] = useState(false);
  const [unlinked, setUnlinked] = useState(0); // [marker: fixes-oct21 v1]
  useEffect(() => { api.get('/sponsor-links/count').then(r => setUnlinked(r.data?.count || 0)).catch(() => {}); }, []);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/events?limit=5000');
      setEvents(res.data.data || []);
    } catch { toast.error('Failed to load events.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const toggleUpcoming = (v) => {
    setUpcomingOnly(v);
    try { localStorage.setItem('events:upcomingOnly', v ? 'true' : 'false'); } catch {}
  };
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const shown = upcomingOnly
    ? events.filter(ev => ev.event_date && new Date(String(ev.event_date).slice(0, 10) + 'T00:00:00') >= today)
    : events;

  const columns = [
    { key: 'name', label: 'Event', width: 280, render: ev => <strong>{ev.name}</strong> },
    { key: 'event_date', label: 'Date', width: 110, type: 'date', get: ev => (ev.event_date ? String(ev.event_date).slice(0, 10) + 'T00:00:00' : null) },
    { key: 'start_time', label: 'Start', width: 80, text: ev => fmtTime(ev.start_time) },
    { key: 'venue', label: 'Venue', width: 200 },
    { key: 'location_address', label: 'Address', width: 220, defaultHidden: true },
    { key: 'tickets_sold', label: 'Tickets Sold', width: 100, type: 'number' },
    { key: 'total_capacity', label: 'Capacity', width: 90, type: 'number' },
    { key: 'checked_in_count', label: 'Checked In', width: 95, type: 'number', defaultHidden: true },
    { key: 'sponsorship_count', label: 'Sponsors', width: 90, type: 'number' },
    { key: 'sponsorship_revenue', label: 'Sponsor $ Paid', width: 120, type: 'money', align: 'right' },
    { key: 'status', label: 'Status', width: 100, render: ev => <span className={`badge badge-${ev.status}`}>{ev.status}</span> },
    { key: 'visibility', label: 'Visibility', width: 90, defaultHidden: true },
    { key: 'created_by', label: 'Created By', width: 130, defaultHidden: true, get: ev => [ev.created_by_first_name, ev.created_by_last_name].filter(Boolean).join(' ') },
  ];

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Events</h1>
          <p className="page-subtitle">{shown.length} {upcomingOnly ? 'upcoming ' : ''}events</p>
        </div>
        {isManagerOrAbove() && <button className="btn btn-primary" onClick={() => setShowModal(true)}>+ Create Event</button>}
      </div>
      {unlinked > 0 && (
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '8px 12px', marginBottom: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <span>🔗 {unlinked} sponsorship invoice line{unlinked === 1 ? ' isn’t' : 's aren’t'} linked to an event yet, so {unlinked === 1 ? 'it doesn’t' : 'they don’t'} show as sponsors.</span>
          <button className="btn btn-primary btn-sm" onClick={() => navigate('/sponsor-links')}>Link them</button>
        </div>
      )}

      <DataGrid
        gridKey="events"
        rows={shown}
        loading={loading}
        columns={columns}
        onRowClick={ev => navigate(`/events/${ev.id}`)}
        totals={['tickets_sold', 'sponsorship_revenue']}
        emptyText={upcomingOnly ? 'No upcoming events. Untick "Upcoming only" to see past events.' : 'No events found.'}
        toolbarLeft={
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.875rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={upcomingOnly} onChange={e => toggleUpcoming(e.target.checked)} />
            Upcoming only
          </label>
        }
      />

      {showModal && <CreateEventModal onClose={() => setShowModal(false)} onSaved={() => { setShowModal(false); fetchEvents(); }} />}
    </div>
  );
}

function fmtTime(t) {
  if (!t) return '';
  const [h, m] = String(t).split(':').map(Number);
  if (isNaN(h)) return String(t);
  return `${((h + 11) % 12) + 1}:${String(m || 0).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
}

function CreateEventModal({ onClose, onSaved }) {
  // [marker: fixes-oct21 v1] by default a new tag is made for this event's ticket buyers
  const [form, setForm] = useState({ name: '', eventDate: '', startTime: '', venue: '', locationAddress: '', totalCapacity: '', visibility: 'public', status: 'draft', attendeeTagId: '__new__' });
  const [newTagName, setNewTagName] = useState('');
  const [tagNameTouched, setTagNameTouched] = useState(false);
  const suggestedTag = (form.name.trim() ? `${form.name.trim().slice(0, 48)} – attendees` : '').slice(0, 60);
  const tagName = tagNameTouched ? newTagName : suggestedTag;
  const [saving, setSaving] = useState(false);
  const [tags, setTags] = useState([]);
  const set = (k, v) => setForm(f => ({...f, [k]: v}));

  useEffect(() => {
    api.get('/contact-tags')
      .then(r => setTags((r.data || []).filter(t => t.is_active !== false)))
      .catch(() => {});
  }, []);

  const handleSave = async () => {
    if (!form.name || !form.eventDate) return toast.error('Event name and date are required.');
    if (form.attendeeTagId === '__new__' && !tagName.trim()) return toast.error('Name the ticket-buyer tag (or pick “No tag”).');
    setSaving(true);
    try {
	let attendeeTagId = form.attendeeTagId === '__new__' ? null : (form.attendeeTagId || null);
	if (form.attendeeTagId === '__new__') {
	  try {
	    attendeeTagId = (await api.post('/contact-tags', { name: tagName.trim(), color: '#2563eb', description: `Ticket buyers for ${form.name.trim()}` })).data.id;
	  } catch (e) {
	    if (e.response?.status !== 409) throw e;
	    const existing = (tags || []).find(t => t.name.toLowerCase() === tagName.trim().toLowerCase());
	    if (!existing) throw e;
	    attendeeTagId = existing.id; // a tag with that name already exists — use it
	  }
	}
	await api.post('/events', {
	  ...form,
	  totalCapacity: form.totalCapacity || null,
	  attendeeTagId,
	});
      toast.success('Event created.');
      onSaved();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to create event.'); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">Create Event</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Event Name *</label>
            <input className="form-input" value={form.name} onChange={e => set('name', e.target.value)} />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Date *</label>
              <input className="form-input" type="date" value={form.eventDate} onChange={e => set('eventDate', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Start Time</label>
              <input className="form-input" type="time" value={form.startTime} onChange={e => set('startTime', e.target.value)} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Venue</label>
              <input className="form-input" value={form.venue} onChange={e => set('venue', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Capacity</label>
              <input className="form-input" type="number" value={form.totalCapacity} onChange={e => set('totalCapacity', e.target.value)} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Visibility</label>
              <select className="form-select" value={form.visibility} onChange={e => set('visibility', e.target.value)}>
                <option value="public">Public</option>
                <option value="members_only">Members Only</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select className="form-select" value={form.status} onChange={e => set('status', e.target.value)}>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Tag for ticket buyers</label>
            <select className="form-select" value={form.attendeeTagId} onChange={e => set('attendeeTagId', e.target.value)}>
              <option value="__new__">✚ Create a new tag for this event</option>
              <option value="">— No tag —</option>
              {tags.length > 0 && <optgroup label="Use an existing tag">{tags.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}</optgroup>}
            </select>
            {form.attendeeTagId === '__new__' && (
              <input className="form-input" style={{ marginTop: 6 }} maxLength={60} placeholder="Tag name, e.g. 2026 Gala – attendees"
                     value={tagName} onChange={e => { setTagNameTouched(true); setNewTagName(e.target.value); }} />
            )}
            <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: 4 }}>
              Everyone who buys or registers for this event is automatically given this tag.
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Create Event'}</button>
        </div>
      </div>
    </div>
  );
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
